import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet,Image } from '@react-pdf/renderer';
import img from '../images-src/logo.jpeg';

const InvoicesList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [searchInvoiceNo, setSearchInvoiceNo] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateInvoiceTotal = (invoice) => {
    const lines = invoice.ilines || invoice.items || [];
    if (invoice.total) return parseFloat(invoice.total);
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const subtotal = quantity * unitPrice;
      const afterDiscount = subtotal - discount;
      const vat = afterDiscount * (vatPercent / 100);
      const total = afterDiscount + vat;
      return sum + total;
    }, 0);
  };

  const calculateInvoiceVAT = (invoice) => {
    const lines = invoice.ilines || invoice.items || [];
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      const vat = afterDiscount * (vatPercent / 100);
      return sum + vat;
    }, 0);
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${apiUrl}/api/invoices`);
        const payload = response.data;
        const invoiceList = Array.isArray(payload)
          ? payload
          : payload.data || [];
        setInvoices(invoiceList);
      } catch (err) {
        console.error('Error fetching invoices:', err);
        setError('Unable to load invoices. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [apiUrl]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Paid: 'badge-success',
      Unpaid: 'badge-warning',
      Pending: 'badge-info',
      Overdue: 'badge-error',
    };
    return `badge ${statusClasses[status] || 'badge-ghost'} badge-sm`;
  };

  // Filter invoices based on search and status
  const filteredInvoices = invoices.filter(invoice => {
    const invoiceNo = (invoice.invoice_no || invoice.invoiceNo || '').toLowerCase();
    const customerName = (invoice.customer?.name || invoice.customerName || '').toLowerCase();
    const status = invoice.status || '';

    const matchesInvoiceNo = searchInvoiceNo === '' || invoiceNo.includes(searchInvoiceNo.toLowerCase());
    const matchesCustomer = searchCustomer === '' || customerName.includes(searchCustomer.toLowerCase());
    const matchesStatus = statusFilter === 'All' || status === statusFilter;

    return matchesInvoiceNo && matchesCustomer && matchesStatus;
  });

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await axios.delete(`${apiUrl}/api/invoices/${invoiceId}`);
        setInvoices(invoices.filter(invoice => invoice.id !== invoiceId));
      } catch (err) {
        console.error('Error deleting invoice:', err);
        setError('Failed to delete invoice. Please try again.');
      }
    }
  };

  const invoicePdfStyles = StyleSheet.create({
    page: {
      padding: 10,
      fontSize: 8,
      fontFamily: 'Helvetica',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      borderBottomWidth: 2,
      borderBottomColor: '#0891b2',
      paddingBottom: 20,
    },
    companyName: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#0891b2',
    },
    companyDetails: {
      fontSize: 9,
      color: '#333',
      marginTop: 2,
    },
    invoiceTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
    },
    subtitle0: {
      fontSize: 10,
      color: '#666',
      textAlign: 'right',
      marginTop: 5,
    fontWeight: 'bold',
    },
    subtitle: {
      fontSize: 8,
      color: '#666',
      textAlign: 'right',
      marginTop: 5,
    },
    infoSection: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 15,
      backgroundColor: '#ffffff',
      padding: 10,
    },
    infoBlock: {
      flex: 1,
      marginRight: 15,
    },
    infoLabel: {
      fontSize: 9,
      color: '#333',
      marginBottom: 2,
      textTransform: 'uppercase',
      fontWeight: 'bold',
    },
    infoValue: {
      fontSize: 11,
      fontWeight: '600',
      color: '#000',
    },
    customerSection: {
      marginBottom: 20,
      padding: 10,
      backgroundColor: '#ffffff',
    },
    table: {
      marginBottom: 20,
      borderWidth: 1,
      borderColor: '#ddd',
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#0891b2',
      color: 'white',
      padding: 10,
      fontWeight: 'bold',
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#ddd',
      padding: 8,
    },
    tableRowAlt: {
      backgroundColor: '#f9f9f9',
    },
    col1: { width: '35%', paddingHorizontal: 5 },
    col2: { width: '10%', textAlign: 'center', paddingHorizontal: 5 },
    col3: { width: '15%', textAlign: 'right', paddingHorizontal: 5 },
    col4: { width: '15%', textAlign: 'right', paddingHorizontal: 5 },
    col5: { width: '10%', textAlign: 'center', paddingHorizontal: 5 },
    col6: { width: '15%', textAlign: 'right', paddingHorizontal: 5 },
    totals: {
      marginTop: 20,
      alignItems: 'flex-end',
    },
    totalRow: {
      flexDirection: 'row',
      padding: 5,
      width: '100%',
    },
    totalRowHalf: {
      flexDirection: 'row',
      padding: 5,
      width: '48%',
      marginRight: '2%',
      marginBottom: 5,
    },
    totalLabel: {
      flex: 1,
      textAlign: 'right',
      marginRight: 10,
      fontSize: 11,
    },
    totalValue: {
      width: 100,
      textAlign: 'right',
      fontSize: 11,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
    },
    summaryBox: {
      width: '48%',
      padding: 10,
      backgroundColor: '#f0f9fc',
      borderWidth: 1,
      borderColor: '#0891b2',
    },
    summaryLabel: {
      fontSize: 10,
      color: '#666',
      marginBottom: 5,
      fontWeight: 'bold',
    },
    summaryValue: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
    },
    grandTotal: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#0891b2',
      borderTopWidth: 2,
      borderTopColor: '#0891b2',
      paddingTop: 10,
      marginTop: 10,
      width: '100%',
    },
    notes: {
      marginTop: 30,
      padding: 15,
      backgroundColor: '#f8f9fa',
    },
    notesTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      marginBottom: 8,
      color: '#0891b2',
    },
    notesText: {
      fontSize: 10,
      lineHeight: 1.4,
      color: '#666',
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
      right: 30,
      textAlign: 'center',
      color: '#666',
      fontSize: 9,
      borderTopWidth: 1,
      borderTopColor: '#ddd',
      paddingTop: 10,
    },
    compliance: {
      fontSize: 8,
      color: '#0891b2',
      marginTop: 5,
    },
  });

  const formatCurrencyPdf = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const InvoicePDFDocument = ({ invoice }) => {
    const lines = (invoice && (invoice.ilines || invoice.items)) || [];
    const customerName = (invoice && (invoice.customer?.name || invoice.customerName)) || 'Unknown Customer';
    const invoiceDate = invoice && (invoice.invoice_date || invoice.date);
    const dueDate = invoice && (invoice.due_date || invoice.dueDate);
    const paymentTerms = (invoice && (invoice.payment_terms || invoice.paymentTerms)) || 'Paid';
    
    const total = calculateInvoiceTotal(invoice || {});
    const subtotal = lines.reduce((sum, line) => {
      const quantity = parseFloat(line?.quantity || 0);
      const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
      return sum + (quantity * unitPrice);
    }, 0);
    const totalDiscount = lines.reduce((sum, line) => {
      return sum + parseFloat(line?.discount_amount || line?.discountAmount || 0);
    }, 0);
    const taxableAmount = lines.reduce((sum, line) => {
      const quantity = parseFloat(line?.quantity || 0);
      const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
      const discount = parseFloat(line?.discount_amount || line?.discountAmount || 0);
      return sum + (quantity * unitPrice - discount);
    }, 0);
    const vatTotal = lines.reduce((sum, line) => {
      const quantity = parseFloat(line?.quantity || 0);
      const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
      const discount = parseFloat(line?.discount_amount || line?.discountAmount || 0);
      const vatPercent = parseFloat(line?.vat_percent || line?.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      return sum + (afterDiscount * (vatPercent / 100));
    }, 0);

    return (
      <Document>
        <Page size="A4" style={invoicePdfStyles.page}>
          <View style={invoicePdfStyles.header}>
            <View>
                <Image src={img} style={{ width: 90, height: 110, marginBottom: 5,marginLeft: 100 }} />
              {/* <Text style={invoicePdfStyles.companyName}>Diamond Quality Constructions</Text>
              <Text style={invoicePdfStyles.companyDetails}>Sharja, UAE</Text>
              <Text style={invoicePdfStyles.companyDetails}>TRN: 104836477000001</Text> */}
            </View>
            <View>
              <Text style={invoicePdfStyles.invoiceTitle}>TAX INVOICE</Text>
              <Text style={invoicePdfStyles.subtitle0}>Diamond Quality Construction L.L.C.SP</Text>
            
          <Text style={invoicePdfStyles.subtitle}>TRN: 104836477000001</Text>

  <Text  style={invoicePdfStyles.subtitle}>Sharjah - Muweilah - University City Street</Text> 
  <Text  style={invoicePdfStyles.subtitle}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
  <Text  style={invoicePdfStyles.subtitle}>0557001522</Text>
  <Text  style={invoicePdfStyles.subtitle}>dqcc01@gmail.com</Text>
  <Text  style={invoicePdfStyles.subtitle}>www.dqcc.ae</Text>
            
            </View>
          </View>

          <View style={invoicePdfStyles.infoSection}>
            <View style={invoicePdfStyles.infoBlock}>
              <Text style={invoicePdfStyles.infoLabel}>Invoice Number</Text>
              <Text style={invoicePdfStyles.infoValue}>{invoice?.invoice_no || invoice?.invoiceNo || 'N/A'}</Text>
            </View>
            <View style={invoicePdfStyles.infoBlock}>
              <Text style={invoicePdfStyles.infoLabel}>Invoice Date</Text>
              <Text style={invoicePdfStyles.infoValue}>{formatDate(invoiceDate)}</Text>
            </View>
            {/* <View style={invoicePdfStyles.infoBlock}>
              <Text style={invoicePdfStyles.infoLabel}>Payment Due</Text>
              <Text style={invoicePdfStyles.infoValue}>{formatDate(dueDate)}</Text>
            </View> */}
            <View style={invoicePdfStyles.infoBlock}>
              <Text style={invoicePdfStyles.infoLabel}>Payment Status</Text>
              <Text style={invoicePdfStyles.infoValue}>{paymentTerms}</Text>
            </View>
            {/* <View style={invoicePdfStyles.infoBlock}>
              <Text style={invoicePdfStyles.infoLabel}>Invoice Amount</Text>
              <Text style={invoicePdfStyles.infoValue}>{formatCurrencyPdf(total)}</Text>
            </View> */}
          </View>

          <View style={invoicePdfStyles.customerSection}>
            <Text style={[invoicePdfStyles.infoLabel, { marginBottom: 8 }]}>CHARGE TO</Text>
            <Text style={[invoicePdfStyles.infoValue, { marginTop: 3, marginBottom: 5 }]}>{customerName}</Text>
            {(invoice?.customer?.address || invoice?.customerAddress) && (
              <Text style={[invoicePdfStyles.companyDetails, { marginBottom: 3 }]}>Address: {invoice?.customer?.address || invoice?.customerAddress}</Text>
            )}
            {(invoice?.customer?.trn || invoice?.customerTrn) && (
              <Text style={[invoicePdfStyles.companyDetails, { marginBottom: 3 }]}>TRN No.: {invoice?.customer?.trn || invoice?.customerTrn}</Text>
            )}
            {(invoice?.customer?.phone || invoice?.customerPhone) && (
              <Text style={invoicePdfStyles.companyDetails}>Phone: {invoice?.customer?.phone || invoice?.customerPhone}</Text>
            )}
          </View>

          <View style={invoicePdfStyles.table}>
            <View style={invoicePdfStyles.tableHeader}>
              <Text style={invoicePdfStyles.col1}>Description</Text>
              <Text style={invoicePdfStyles.col2}>Qty</Text>
              <Text style={invoicePdfStyles.col3}>Unit Price</Text>
              <Text style={invoicePdfStyles.col4}>Discount</Text>
              <Text style={invoicePdfStyles.col5}>VAT</Text>
              <Text style={invoicePdfStyles.col6}>Total</Text>
            </View>
            {lines.map((line, index) => {
              const quantity = parseFloat(line?.quantity || 0);
              const unitPrice = parseFloat(line?.unit_price || line?.unitPrice || 0);
              const discount = parseFloat(line?.discount_amount || line?.discountAmount || 0);
              const vatPercent = parseFloat(line?.vat_percent || line?.vatPercent || 0);
              const subtotalLine = quantity * unitPrice;
              const afterDiscount = subtotalLine - discount;
              const vat = afterDiscount * (vatPercent / 100);
              const totalLine = afterDiscount + vat;
              return (
                <View key={index} style={[invoicePdfStyles.tableRow, index % 2 === 1 ? invoicePdfStyles.tableRowAlt : {}]}>
                  <Text style={invoicePdfStyles.col1}>{line?.description || 'Item'}</Text>
                  <Text style={invoicePdfStyles.col2}>{quantity}</Text>
                  <Text style={invoicePdfStyles.col3}>{formatCurrencyPdf(unitPrice)}</Text>
                  <Text style={invoicePdfStyles.col4}>{formatCurrencyPdf(discount)}</Text>
                  <Text style={invoicePdfStyles.col5}>{vatPercent}%</Text>
                  <Text style={invoicePdfStyles.col6}>{formatCurrencyPdf(totalLine)}</Text>
                </View>
              );
            })}
          </View>

          <View style={invoicePdfStyles.totals}>
            <View style={invoicePdfStyles.totalRow}>
              <Text style={invoicePdfStyles.totalLabel}>Subtotal:</Text>
              <Text style={invoicePdfStyles.totalValue}>{formatCurrencyPdf(subtotal)}</Text>
            </View>
            <View style={invoicePdfStyles.totalRow}>
              <Text style={invoicePdfStyles.totalLabel}>Total Discount:</Text>
              <Text style={invoicePdfStyles.totalValue}>{formatCurrencyPdf(totalDiscount)}</Text>
            </View>
            <View style={invoicePdfStyles.totalRow}>
              <Text style={invoicePdfStyles.totalLabel}>Taxable Amount:</Text>
              <Text style={invoicePdfStyles.totalValue}>{formatCurrencyPdf(taxableAmount)}</Text>
            </View>
            <View style={invoicePdfStyles.totalRow}>
              <Text style={invoicePdfStyles.totalLabel}>VAT Amount (5%):</Text>
              <Text style={invoicePdfStyles.totalValue}>{formatCurrencyPdf(vatTotal)}</Text>
            </View>
            <View style={[invoicePdfStyles.totalRow, invoicePdfStyles.grandTotal]}>
              <Text style={invoicePdfStyles.totalLabel}>Total Amount Due:</Text>
              <Text style={invoicePdfStyles.totalValue}>{formatCurrencyPdf(total)}</Text>
            </View>
          </View>

          {invoice?.notes && (
            <View style={invoicePdfStyles.notes}>
              <Text style={invoicePdfStyles.notesTitle}>Notes / Terms:</Text>
              <Text style={invoicePdfStyles.notesText}>{invoice.notes}</Text>
            </View>
          )}

          <View style={invoicePdfStyles.footer}>
            <Text>This is a computer generated invoice</Text>
            {/* <Text style={invoicePdfStyles.compliance}>
              🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN: 104836477000001
            </Text> */}
          </View>
        </Page>
      </Document>
    );
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      // Validate invoice data
      if (!invoice) {
        throw new Error('Invoice data is missing');
      }

      const invoiceNo = invoice.invoice_no || invoice.invoiceNo || invoice.id || 'Invoice';
      
      // Generate PDF with better error handling
      const pdfDocument = React.createElement(InvoicePDFDocument, { invoice });
      const pdfGenerator = pdf(pdfDocument);
      const blob = await pdfGenerator.toBlob();
      
      if (!blob || blob.size === 0) {
        throw new Error('PDF blob is empty');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert(`Unable to generate PDF: ${err.message}`);
    }
  };

  const InvoicesTablePDFDocument = () => {
    const totalAmount = filteredInvoices.reduce((sum, invoice) => sum + calculateInvoiceTotal(invoice), 0);
    const totalVAT = filteredInvoices.reduce((sum, invoice) => sum + calculateInvoiceVAT(invoice), 0);

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={invoicePdfStyles.page}>
          <View style={{ padding: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, color: '#0891b2' }}>
              Invoice Listing Report
            </Text>
            <Text style={{ fontSize: 10, color: '#666', marginBottom: 10 }}>
              Generated on {formatDate(new Date().toISOString())}
            </Text>

            <View style={invoicePdfStyles.table}>
              <View style={invoicePdfStyles.tableHeader}>
                <Text style={{ width: '15%', paddingHorizontal: 5 }}>Invoice No</Text>
                <Text style={{ width: '25%', paddingHorizontal: 5 }}>Customer</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center' }}>Date</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right' }}>Amount</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right' }}>VAT</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center' }}>Status</Text>
              </View>

              {filteredInvoices.map((invoice, index) => (
                <View key={index} style={[invoicePdfStyles.tableRow, index % 2 === 1 ? invoicePdfStyles.tableRowAlt : {}]}>
                  <Text style={{ width: '15%', paddingHorizontal: 5, fontSize: 10 }}>
                    {invoice.invoice_no || invoice.invoiceNo}
                  </Text>
                  <Text style={{ width: '25%', paddingHorizontal: 5, fontSize: 10 }}>
                    {invoice.customer?.name || invoice.customerName || 'Unknown'}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center', fontSize: 10 }}>
                    {formatDate(invoice.invoice_date || invoice.date)}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 10 }}>
                    {formatCurrencyPdf(calculateInvoiceTotal(invoice))}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 10, color: '#b45309' }}>
                    {formatCurrencyPdf(calculateInvoiceVAT(invoice))}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center', fontSize: 9 }}>
                    {invoice.status}
                  </Text>
                </View>
              ))}

              <View style={[invoicePdfStyles.tableRow, { backgroundColor: '#f3f4f6', fontWeight: 'bold' }]}>
                <Text style={{ width: '55%', paddingHorizontal: 5, fontSize: 11, fontWeight: 'bold', textAlign: 'right' }}>
                  TOTAL:
                </Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 11, fontWeight: 'bold' }}>
                  {formatCurrencyPdf(totalAmount)}
                </Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 11, fontWeight: 'bold', color: '#b45309' }}>
                  {formatCurrencyPdf(totalVAT)}
                </Text>
                <Text style={{ width: '15%', paddingHorizontal: 5 }}></Text>
              </View>
            </View>
          </View>
        </Page>
      </Document>
    );
  };

  const handleExportTablePdf = async () => {
    try {
      const pdfDocument = React.createElement(InvoicesTablePDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices-list-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Invoices</h2>
          <p className="text-sm text-gray-600">Manage all your invoices</p>
        </div>
        <Link to="/invoices/create" className="btn btn-primary btn-sm sm:btn-md w-full sm:w-auto">
          ➕ Create Invoice
        </Link>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Invoice No
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter invoice number..."
              value={searchInvoiceNo}
              onChange={(e) => setSearchInvoiceNo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Customer
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter customer name..."
              value={searchCustomer}
              onChange={(e) => setSearchCustomer(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Status
            </label>
            <select
              className="select select-bordered select-sm w-full"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Pending">Pending</option>
              <option value="Overdue">Overdue</option>
            </select>
          </div>
        </div>
        {(searchInvoiceNo || searchCustomer || statusFilter !== 'All') && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {filteredInvoices.length} of {invoices.length} invoices
            </span>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => {
                setSearchInvoiceNo('');
                setSearchCustomer('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Loading invoices from the server...
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Invoices Table - Desktop View */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden hidden md:block">
        {filteredInvoices.length > 0 && (
          <div className="p-4 border-b border-gray-200 flex justify-end">
            <button
              onClick={handleExportTablePdf}
              className="btn btn-sm btn-outline btn-primary"
            >
              📥 Export Table to PDF
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead className="bg-gray-50">
              <tr>
                <th>Invoice No</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>VAT</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="font-medium text-primary">
                    {invoice.invoice_no || invoice.invoiceNo}
                  </td>
                  <td>
                    {invoice.customer?.name || invoice.customerName || 'Unknown'}
                  </td>
                  <td>
                    {formatDate(invoice.invoice_date || invoice.date)}
                  </td>
                  <td className="font-mono">
                    {formatCurrency(calculateInvoiceTotal(invoice))}
                  </td>
                  <td className="font-mono text-amber-600 font-medium">
                    {formatCurrency(calculateInvoiceVAT(invoice))}
                  </td>
                  <td>
                    <span className={getStatusBadge(invoice.status)}>
                      {invoice.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => setPreviewInvoice(invoice)}
                      >
                        👁️ View
                      </button>
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => handleDownloadPdf(invoice)}
                      >
                        📄 PDF
                      </button>
                      <button 
                        className="btn btn-xs btn-ghost btn-info text-white"
                        onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn btn-xs btn-ghost btn-error text-white"
                        onClick={() => handleDelete(invoice.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredInvoices.length > 0 && (
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td colSpan="3" className="text-right pr-4">TOTAL:</td>
                  <td className="font-mono text-gray-900">
                    {formatCurrency(
                      filteredInvoices.reduce((sum, invoice) => sum + calculateInvoiceTotal(invoice), 0)
                    )}
                  </td>
                  <td className="font-mono text-amber-600">
                    {formatCurrency(
                      filteredInvoices.reduce((sum, invoice) => sum + calculateInvoiceVAT(invoice), 0)
                    )}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredInvoices.length === 0 && invoices.length > 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching invoices</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setSearchInvoiceNo('');
                setSearchCustomer('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {invoices.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📄</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600 mb-4">Create your first invoice to get started</p>
            <Link to="/invoices/create" className="btn btn-primary">
              Create Your First Invoice
            </Link>
          </div>
        )}
      </div>

      {/* Invoices Cards - Mobile View */}
      <div className="md:hidden space-y-3">
        {filteredInvoices.length === 0 && invoices.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching invoices</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setSearchInvoiceNo('');
                setSearchCustomer('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📄</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600 mb-4">Create your first invoice to get started</p>
            <Link to="/invoices/create" className="btn btn-primary btn-sm">
              Create Your First Invoice
            </Link>
          </div>
        ) : (
          filteredInvoices.map((invoice) => (
            <div key={invoice.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div>
                    <p className="font-medium text-primary text-sm">
                      {invoice.invoice_no || invoice.invoiceNo}
                    </p>
                    <p className="text-gray-600 text-xs">
                      {invoice.customer?.name || invoice.customerName || 'Unknown'}
                    </p>
                  </div>
                </div>
                <span className={getStatusBadge(invoice.status)}>
                  {invoice.status}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(invoice.invoice_date || invoice.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500">Amount</p>
                  <p className="font-medium font-mono">{formatCurrency(calculateInvoiceTotal(invoice))}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  className="btn btn-xs btn-ghost flex-1"
                  onClick={() => setPreviewInvoice(invoice)}
                >
                  👁️ View
                </button>
                <button
                  className="btn btn-xs btn-ghost flex-1"
                  onClick={() => handleDownloadPdf(invoice)}
                >
                  📄 PDF
                </button>
                <button 
                  className="btn btn-xs btn-ghost btn-info text-white flex-1"
                  onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="btn btn-xs btn-ghost btn-error text-white flex-1"
                  onClick={() => handleDelete(invoice.id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {previewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/10">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold">Invoice Preview</h3>
                <p className="text-sm text-gray-500">
                  {previewInvoice.invoice_no || previewInvoice.invoiceNo}
                </p>
              </div>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setPreviewInvoice(null)}
              >
                Close
              </button>
            </div>

            <div className="space-y-6 p-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Invoice</p>
                  <p className="font-semibold text-gray-900">
                    {previewInvoice.invoice_no || previewInvoice.invoiceNo}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Date</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(previewInvoice.invoice_date || previewInvoice.date)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                  <span className={getStatusBadge(previewInvoice.status)}>
                    {previewInvoice.status}
                  </span>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Customer</p>
                  <p className="font-semibold text-gray-900">
                    {previewInvoice.customer?.name || previewInvoice.customerName || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {previewInvoice.customer?.phone || previewInvoice.customerPhone || ''}
                  </p>
                  <p className="text-sm text-gray-600">
                    {previewInvoice.customer?.address || previewInvoice.customerAddress || ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Payment</p>
                  <p className="font-semibold text-gray-900">{previewInvoice.mode}</p>
                  <p className="text-sm text-gray-600">Due date: {formatDate(previewInvoice.due_date)}</p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">VAT %</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(previewInvoice.ilines || previewInvoice.items || []).map((line, lineIndex) => {
                      const quantity = parseFloat(line.quantity || 0);
                      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
                      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
                      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
                      const subtotal = quantity * unitPrice;
                      const afterDiscount = subtotal - discount;
                      const vat = afterDiscount * (vatPercent / 100);
                      const total = afterDiscount + vat;

                      return (
                        <tr key={lineIndex}>
                          <td className="px-4 py-3 text-sm text-gray-700">{line.description}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{quantity}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(unitPrice)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(discount)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{vatPercent}%</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{formatCurrency(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-4 justify-end md:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Subtotal</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {formatCurrency(calculateInvoiceTotal(previewInvoice))}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Notes</p>
                  <p className="mt-2 text-sm text-gray-700">{previewInvoice.notes || 'No notes provided'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesList;